import {
  Inject,
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  ViewChildren,
  AfterViewInit,
  Input,
  Output,
  EventEmitter
} from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject, of } from 'rxjs';
import { catchError, map, take, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import {
  ChannelUIds,
  MassNotificationTemplate,
  CommunicationTemplate,
  MassNotification,
  MassNotificationRecipient,
  TemplateViewControlType
} from 'src/app/modules/communications/store/communications/communications.model';
import * as fromComm from 'src/app/modules/communications/store/communications';
import { Workflow, WorkflowParentType, WorkflowStepAction } from 'src/app/modules/core/store/models/workflow.model';
import { ChannelSelectionService } from 'src/app/modules/communications/services/channel-selection-service';
import { CultureService } from 'src/app/modules/core/services/culture.service';
import { Dataset, DatasetSubtype, DatasetType } from 'src/app/modules/core/store/models/dataset.model';
import { DatasetApiService } from 'src/app/modules/core/services/dataset-api.service';
import { DataApiService } from 'src/app/modules/management/store/data/data-api.service';
import { GridHelperService } from 'src/app/modules/core/services/grid-helper.service';
import {
  Document,
  DocumentQueryParams,
  DocumentReader,
  FieldDataType,
  FilterGroup,
  FilterGroupType,
  QueryParams,
  RuleOperator,
  SchemaField,
  SystemFieldType,
  getDefaultSchemaFields
} from 'src/app/modules/core/store/models';
import { DataTableServerComponent, SortEvent } from '@app/modules/core/components/data-table-server/data-table.component';
import { Column, GetRowIdParams, IServerSideDatasource, ValueFormatterParams, ValueGetterParams } from 'ag-grid-enterprise';
import { getCellFilter, getCellRenderer, getColumnFilterParams, getValueGetter } from 'src/app/modules/core/components/ag-grid';
import { PicklistService } from '@app/modules/core/services/picklist/picklist.service';
import { v4 as uuidv4 } from 'uuid';
import { LogStatusUIds } from './notification-log.model';
import { TableColumnsService } from '@app/modules/cop/services/table/table-columns-service';
import { NotificationStatusService } from '@app/modules/communications/services/notification-status-service';
import * as XLSX from 'xlsx';

@Component({
  selector: 'topo-comms-notification-log',
  templateUrl: './notification-log.component.html',
  styleUrls: ['./notification-log.component.scss'],
  standalone: false
})
export class NotificationLogComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('table') dataTable: DataTableServerComponent;
  datasource: any;
  columDefs: any[] = [];
  isTableLoading: boolean;
  skip = 0;
  take = 10;
  searchFilterGroup: FilterGroup;
  columnFilterGroup: FilterGroup;
  totalCount: number = 0;
  statusDocument: Document;
  statusTitle: string;
  private pageLinks: { [skip: number]: any };

  destroy$ = new Subject<void>();

  _notification: MassNotification;
  @Input() set notification(value: MassNotification) {
    this.gridHelper.clearSearchFilter();
    this.searchFilterGroup = null;
    this.isTableLoading = true;
    this._notification = value;
  }
  get notification(): MassNotification {
    return this._notification;
  }
  @Input() configuredChannelUId: string;
  @Input() recipientUId: string;

  @Output() logClosed = new EventEmitter();

  constructor(
    private translateService: TranslateService,
    private channelSelectionService: ChannelSelectionService,
    private dataApiService: DataApiService,
    private gridHelper: GridHelperService,
    private picklistService: PicklistService,
    private tableColumnsService: TableColumnsService,
    private cultureService: CultureService,
    private notificationStatusService: NotificationStatusService
  ) {}

  ngOnInit(): void {
    this.datasource = this.getServerSideDatasource();
    this.columDefs = this.generateColumns();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  close(): void {
    this.logClosed.emit();
  }

  onGridDataLoaded(): void {
    this.dataTable?.gridApi?.sizeColumnsToFit();
    this.dataTable?.groupRows(false);
    this.isTableLoading = false;
  }

  onPaginate(skip: number): void {
    this.skip = skip;
  }

  onTakeChanged(take: number): void {
    this.take = take;
    this.skip = 0;
    this.pageLinks = {};
  }

  onSortChanged(sortEvent: SortEvent): void {
    this.skip = 0;
    this.pageLinks = {};
  }

  onSearchFilterChanged(filter: FilterGroup): void {
    this.searchFilterGroup = filter;
    this.skip = 0;
    this.pageLinks = {};
  }

  ngAfterViewInit() {
    this.dataTable?.groupRows(false);
  }

  onExportToExcel(columnNames?: string[]): void {
    this.exportToExcelFromClient();
  }

  private exportContext: { rows: any[]; query: DocumentQueryParams; totalCount: number; columnSettings: any[]; columns: Column[] };
  private exportPageLinks: { [skip: number]: any };

  exportToExcelFromClient() {
    this.exportContext = {
      rows: [],
      query: {
        pagination: {
          skip: 0,
          take: 1000
        },
        sort: 'recipient_name asc,uid desc'
      } as DocumentQueryParams,
      totalCount: 10000,
      columnSettings: [],
      columns: []
    };

    this.exportPageLinks = {};

    const combinedFilterGroup: FilterGroup = {
      filterGroups: [],
      filters: [
        {
          field: 'notification_uid',
          operator: RuleOperator.Equal,
          value: this.notification.uId
        },
        {
          field: 'display_status_uid',
          operator: RuleOperator.NotEqual,
          value: LogStatusUIds.RuleNotificationDeleted
        }
      ],
      groupType: FilterGroupType.And
    };

    if (!!this.columnFilterGroup && (this.columnFilterGroup.filters?.length || this.columnFilterGroup.filterGroups?.length)) {
      combinedFilterGroup.filterGroups.push(this.columnFilterGroup);
    }
    if (!!this.searchFilterGroup && (this.searchFilterGroup.filters?.length || this.searchFilterGroup.filterGroups?.length)) {
      combinedFilterGroup.filterGroups.push(this.columnFilterGroup);
    }
    if (combinedFilterGroup.filters.length || combinedFilterGroup.filterGroups.length) {
      this.exportContext.query.filterGroup = combinedFilterGroup;
    }

    const headerRow = [];
    const columns = this.dataTable.gridApi
      ?.getAllDisplayedColumns()
      ?.filter(c => c.getColId() !== '_selectionChecks' && c.getColId() !== '_actions');
    this.exportContext.columns = columns;

    for (const column of columns) {
      headerRow.push(column.getDefinition().headerName);

      this.exportContext.columnSettings.push({
        wpx: 175
      });
    }
    this.exportContext.rows.push(headerRow);

    this.exportToExcelFromClientBatch();
  }

  exportToExcelFromClientBatch() {
    if (this.exportContext.totalCount > this.exportContext.query.pagination.skip) {
      this.dataApiService.getSystemLogDocuments(DatasetSubtype.MassNotification, this.exportContext.query).subscribe(res => {
        this.exportContext.totalCount = res.totalCount;
        this.populateExportData(res.data);
      });
    } else {
      const ws: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(this.exportContext.rows);
      ws['!cols'] = this.exportContext.columnSettings;

      /* generate workbook and add the worksheets */
      const wb: XLSX.WorkBook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

      /* save to file */
      const fileName = `${this.notification.name}-log-export`;
      XLSX.writeFile(wb, `${fileName}.xlsx`);
    }
  }

  private populateExportData(documents: Document[]): void {
    if (documents.length > 0 && !!documents[documents.length - 1]['__sort']) {
      if (!!!this.exportPageLinks) {
        this.exportPageLinks = {};
      }
      this.exportPageLinks[this.exportContext.query.pagination.skip + this.exportContext.query.pagination.take] =
        documents[documents.length - 1]['__sort'];
    }

    documents.forEach(data => {
      const rowData = [];
      for (const column of this.exportContext.columns) {
        let colId = column.getColId();
        let colData = data[colId];
        if (!!colData) {
          if (colId == 'display_status_text') {
            colData = this.translateService.instant(`COMMUNICATIONS.ERRORS.${colData}`);
          } else if (colId == 'channel_uid') {
            colData = this.channelSelectionService.getFullChannelName(colData);
          } else if (colId == 'date_created') {
            colData = this.cultureService.getDateDisplay(colData, 'LONG-DATE-WITH-TIME');
          }
        }

        rowData.push(colData);
      }
      this.exportContext.rows.push(rowData);
    });

    this.exportContext.query.pagination.skip += this.exportContext.query.pagination.take;
    if (!!this.exportPageLinks && this.exportPageLinks[this.exportContext.query.pagination.skip]) {
      this.exportContext.query.pagination.options = {};
      this.exportContext.query.pagination.options.searchAfter = this.exportPageLinks[this.exportContext.query.pagination.skip];
    }

    this.exportToExcelFromClientBatch();
  }

  private generateColumns() {
    var columns = [];

    const recpField = {
      dataType: FieldDataType.Text,
      displayName: this.translateService.instant('COMMUNICATIONS.COLUMNS.RECIPIENT'),
      systemName: 'recipient_name',
      uId: uuidv4(),
      isUniqueKey: false,
      fieldType: SystemFieldType.System,
      picklistValues: [],
      sourceUnit: null,
      settings: null
    } as SchemaField;

    let valueGetter = getValueGetter(FieldDataType.Text);
    let cellRenderer = getCellRenderer(FieldDataType.Text);
    let filter = getCellFilter(FieldDataType.Text);
    columns.push({
      headerName: this.translateService.instant('COMMUNICATIONS.COLUMNS.RECIPIENT'),
      field: 'recipient_name',
      colId: 'recipient_name',
      sortable: true,
      resizable: true,
      filterParams: getColumnFilterParams(filter),
      filter: filter === 'agSetColumnFilter' ? 'agTextColumnFilter' : filter,
      floatingFilter: false,
      menuTabs: ['filterMenuTab'],
      maxWidth: '350',
      cellRenderer,
      cellRendererParams: {
        schemaField: recpField,
        picklistService: this.picklistService
      },
      valueGetter
    });

    const channelField = {
      dataType: FieldDataType.UId,
      displayName: this.translateService.instant('COMMUNICATIONS.COLUMNS.CHANNEL'),
      systemName: 'channel_uid',
      uId: uuidv4(),
      isUniqueKey: false,
      fieldType: SystemFieldType.System,
      picklistValues: [],
      sourceUnit: null,
      settings: null
    } as SchemaField;

    valueGetter = this.getChannelValueDisplay;
    cellRenderer = 'enumTranslateCellComponent';
    filter = getCellFilter(FieldDataType.Text);
    const channelColumn = {
      headerName: this.translateService.instant('COMMUNICATIONS.COLUMNS.CHANNEL'),
      field: 'channel_uid',
      colId: 'channel_uid',
      sortable: true,
      resizable: true,
      filterParams: getColumnFilterParams(filter),
      filter: filter === 'agSetColumnFilter' ? 'agTextColumnFilter' : filter,
      floatingFilter: false,
      menuTabs: ['filterMenuTab'],
      maxWidth: '350',
      cellRenderer,
      cellRendererParams: {
        schemaField: channelField,
        picklistService: this.picklistService
      },
      valueGetter
    };

    (channelColumn as any).keyCreator = params => {
      return this.getLogChannelValueDisplay(params);
    };
    (channelColumn as any).filter = 'agSetColumnFilter';
    (channelColumn.filterParams as any).values = Object.values(this.getLogChannelValues());
    (channelColumn.filterParams as any).valueGetter = params => {
      return this.getLogChannelValueDisplay(params);
    };

    columns.push(channelColumn);

    const addressField = {
      dataType: FieldDataType.Text,
      displayName: this.translateService.instant('COMMUNICATIONS.COLUMNS.ADDRESS'),
      systemName: 'address',
      uId: uuidv4(),
      isUniqueKey: false,
      fieldType: SystemFieldType.System,
      picklistValues: [],
      sourceUnit: null,
      settings: null
    } as SchemaField;

    valueGetter = getValueGetter(FieldDataType.Text);
    cellRenderer = getCellRenderer(FieldDataType.Text);
    filter = getCellFilter(FieldDataType.Text);
    columns.push({
      headerName: this.translateService.instant('COMMUNICATIONS.COLUMNS.ADDRESS'),
      field: 'address',
      colId: 'address',
      sortable: true,
      resizable: true,
      maxWidth: '300',
      filterParams: getColumnFilterParams(filter),
      filter: filter === 'agSetColumnFilter' ? 'agTextColumnFilter' : filter,
      floatingFilter: false,
      menuTabs: ['filterMenuTab'],
      cellRenderer,
      cellRendererParams: {
        schemaField: addressField,
        picklistService: this.picklistService
      },
      valueGetter
    });

    const dateField = {
      dataType: FieldDataType.DateTime,
      displayName: this.translateService.instant('COMMUNICATIONS.COLUMNS.ADDRESS'),
      systemName: 'date_created',
      uId: uuidv4(),
      isUniqueKey: false,
      fieldType: SystemFieldType.System,
      picklistValues: [],
      sourceUnit: null,
      settings: null
    } as SchemaField;

    valueGetter = getValueGetter(FieldDataType.DateTime);
    cellRenderer = getCellRenderer(FieldDataType.DateTime);
    filter = getCellFilter(FieldDataType.DateTime);
    columns.push({
      headerName: this.translateService.instant('COMMUNICATIONS.COLUMNS.CREATED-ON'),
      field: 'date_created',
      colId: 'date_created',
      sortable: true,
      resizable: true,
      maxWidth: '300',
      filterParams: getColumnFilterParams(filter),
      filter: filter === 'agSetColumnFilter' ? 'agTextColumnFilter' : filter,
      floatingFilter: false,
      menuTabs: ['filterMenuTab'],
      cellRenderer,
      cellRendererParams: {
        schemaField: dateField,
        picklistService: this.picklistService
      },
      valueGetter
    });

    const statusField = {
      dataType: FieldDataType.Text,
      displayName: this.translateService.instant('COMMUNICATIONS.COLUMNS.STATUS'),
      systemName: 'display_status_text',
      uId: uuidv4(),
      isUniqueKey: false,
      fieldType: SystemFieldType.System,
      picklistValues: [],
      sourceUnit: null,
      settings: null
    } as SchemaField;

    valueGetter = getValueGetter(FieldDataType.Text);
    cellRenderer = 'linkCellComponent';
    filter = getCellFilter(FieldDataType.Picklist);
    let statusColumn = {
      headerName: this.translateService.instant('COMMUNICATIONS.COLUMNS.STATUS'),
      field: 'display_status_text',
      colId: 'display_status_text',
      sortable: true,
      resizable: true,
      filterParams: getColumnFilterParams(filter),
      filter: filter === 'agSetColumnFilter' ? 'agTextColumnFilter' : filter,
      floatingFilter: false,
      menuTabs: ['filterMenuTab'],
      minWidth: '350',
      cellRenderer,
      cellRendererParams: {
        sortColumn: 'display_status_text.keyword',
        schemaField: statusField,
        picklistService: this.picklistService,
        clicked: data => {
          this.detailClick(data);
        }
      },
      valueGetter
    };

    (statusColumn as any).keyCreator = params => {
      return this.getLogStatusValueDisplay(params);
    };
    (statusColumn as any).filter = 'agSetColumnFilter';
    (statusColumn.filterParams as any).values = Object.values(this.notificationStatusService.getStatusKeyValues());
    (statusColumn.filterParams as any).valueGetter = params => {
      return this.getLogStatusValueDisplay(params);
    };

    columns.push(statusColumn);

    return columns;
  }

  getLogChannelValueDisplay = (params: ValueGetterParams) => {
    const field = params.colDef.cellRendererParams.schemaField;

    const reader = new DocumentReader(params.data);
    const value = reader.getFieldValue(field.systemName);

    if (!!value) {
      return this.channelSelectionService.getFullChannelName(value);
    }
    return '';
  };

  getLogChannelValues(): { [x: string]: string } {
    const values: { [x: string]: string } = {};
    Object.values(ChannelUIds).forEach(id => {
      values[id] = this.channelSelectionService.getFullChannelName(id);
    });

    return values;
  }

  getLogStatusValueDisplay = (params: ValueGetterParams) => {
    const field = params.colDef.cellRendererParams.schemaField;

    const reader = new DocumentReader(params.data);
    const value = reader.getFieldValue(field.systemName);

    return this.translateService.instant(`COMMUNICATIONS.ERRORS.${value}`);
  };

  detailClick(document: Document) {
    this.statusTitle = document['display_status_text'];
    this.statusDocument = document;
  }

  onStatusClosed(): void {
    this.statusDocument = null;
  }

  getStatusValueDisplay = (params: ValueGetterParams) => {
    const field = params.colDef.cellRendererParams.schemaField;

    const reader = new DocumentReader(params.data);
    const value = reader.getFieldValue(field.systemName);

    return this.translateService.instant(`COMMUNICATIONS.ERRORS.${value}`);
  };

  getChannelValueDisplay = (params: ValueGetterParams) => {
    const field = params.colDef.cellRendererParams.schemaField;

    const reader = new DocumentReader(params.data);
    const value = reader.getFieldValue(field.systemName);

    if (!!value) {
      return this.channelSelectionService.getFullChannelName(value);
    }
    return '';
  };

  getServerSideDatasource(): IServerSideDatasource {
    this.isTableLoading = true;
    return {
      getRows: params => {
        let sortField = 'date_created';
        let sortDirection = 'Descending';

        if (params?.request.sortModel?.length > 0) {
          sortField = params?.request.sortModel[0].colId;
          sortDirection = params?.request.sortModel[0].sort === 'asc' ? 'Ascending' : 'Descending';
        }

        this.columnFilterGroup = null;

        const query = {
          pagination: {
            skip: this.skip,
            take: this.take
          },
          filterGroup: {
            filters: [
              {
                field: 'notification_uid',
                operator: RuleOperator.Equal,
                value: this.notification.uId
              },
              {
                field: 'display_status_uid',
                operator: RuleOperator.NotEqual,
                value: LogStatusUIds.RuleNotificationDeleted
              }
            ],
            filterGroups: []
          },
          sort: sortField + ' ' + sortDirection + ',uid desc',
          fields: null
        } as DocumentQueryParams;

        if (!!params?.request.filterModel) {
          const colFilter = {
            filterGroups: [],
            filters: [],
            groupType: FilterGroupType.And
          };

          for (const fieldName in params?.request.filterModel) {
            if (params?.request.filterModel[fieldName].filterType === 'set') {
              let ruleValues = null;
              if (fieldName === 'display_status_text') {
                const values = this.notificationStatusService.getStatusKeyValues();

                ruleValues = Object.keys(values)
                  .filter(k => params?.request.filterModel[fieldName].values.includes(values[k]))
                  ?.map(p => p);
              } else if (fieldName === 'channel_uid') {
                const values = this.getLogChannelValues();

                ruleValues = Object.keys(values)
                  .filter(k => params?.request.filterModel[fieldName].values.includes(values[k]))
                  ?.map(p => p);
              }

              if (!!ruleValues && ruleValues.length) {
                const orFilter = {
                  filterGroups: [],
                  filters: [],
                  groupType: FilterGroupType.Or
                };

                ruleValues.forEach(v => {
                  orFilter.filters.push({
                    field: fieldName,
                    operator: RuleOperator.Equal,
                    value: v.toString()
                  });
                });

                if (orFilter.filters.length) {
                  colFilter.filterGroups.push(orFilter);
                }
              }
            } else if (params?.request.filterModel[fieldName].type === 'inRange') {
              if (!!params?.request.filterModel[fieldName].dateFrom) {
                query.filterGroup.filters.push({
                  field: fieldName,
                  operator: RuleOperator.DateIsGreaterThan,
                  value: params?.request.filterModel[fieldName].dateFrom
                });
              }
              if (!!params?.request.filterModel[fieldName].dateTo) {
                query.filterGroup.filters.push({
                  field: fieldName,
                  operator: RuleOperator.DateIsLessThan,
                  value: params?.request.filterModel[fieldName].dateTo
                });
              }
            } else {
              const ruleOp = this.tableColumnsService.getRuleOperatorFromOption(params?.request.filterModel[fieldName].type);
              let ruleValue = params?.request.filterModel[fieldName].filter;
              if (!!ruleOp) {
                query.filterGroup.filters.push({
                  field: fieldName,
                  operator: ruleOp,
                  value: ruleValue,
                  filterOptions: null
                });
              }
            }
          }

          this.columnFilterGroup = colFilter;

          if (colFilter.filters.length || colFilter.filterGroups.length) {
            query.filterGroup.filterGroups.push(colFilter);
          }
        }

        if (!!this.searchFilterGroup) {
          query.filterGroup.filterGroups.push(this.searchFilterGroup);
        }

        if (!!this.configuredChannelUId) {
          query.filterGroup.filters.push({
            field: 'configured_channel_uid',
            operator: RuleOperator.Equal,
            value: this.configuredChannelUId
          });
        }

        if (!!this.recipientUId) {
          query.filterGroup.filters.push({
            field: 'recipient_uid',
            operator: RuleOperator.Equal,
            value: this.recipientUId
          });
        }

        if (!!this.pageLinks && this.pageLinks[this.skip]) {
          query.pagination.options = {};
          query.pagination.options.searchAfter = this.pageLinks[this.skip];
        }

        this.dataApiService.getSystemLogDocuments(DatasetSubtype.MassNotification, query).subscribe(
          resp => {
            if (!!!resp) {
              resp = {
                data: [],
                totalCount: 0
              };
            }
            const response = this.gridHelper.queryResultToGridData(resp);
            this.totalCount = response?.rowCount;

            const rowData = response?.rowData?.map(d => {
              const data = {
                ...d,
                display_status_text: this.translateService.instant(`COMMUNICATIONS.ERRORS.${d.display_status_text}`) + '   ',
                extra: ''
              };
              return data;
            });

            if (resp.data?.length > 0 && !!resp?.data[resp.data.length - 1]['__sort']) {
              if (!!!this.pageLinks) {
                this.pageLinks = {};
              }
              this.pageLinks[this.skip + this.take] = resp?.data[resp.data.length - 1]['__sort'];
            }

            params.success({
              rowData: rowData,
              rowCount: response?.rowCount
            });
          },
          err => params.fail()
        );
      }
    };
  }

  getRowId = (params: GetRowIdParams): string => {
    return params.data.uid;
  };
}
